/*
 * Copyright (C) 2024 Reseau Concept.
 * https://reseau-concept.com
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * along with this program. If not, see <http://www.gnu.org/licenses/>.
 */
import { inject, Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { AppConfigService, AuthenticationService } from '@alfresco/adf-core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { supportedEditExtensions, supportedViewExtensions } from '../rules/evaluators';
import { collaboraAuthorizationHeader } from '../collabora-auth';

/** Reponse du webscript `lool/token`. */
export interface LoolToken {
  access_token: string;
  access_token_ttl: string;
  wopi_src_url: string;
}

@Injectable({
  providedIn: 'root'
})
export class CollaboraOnlineService {
  private previousUrl: string;
  private currentUrl: string;
  isLoading = false;
  displayNode: any = null;

  private readonly router = inject(Router);
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthenticationService);
  private readonly appConfig = inject(AppConfigService);

  constructor() {
    this.currentUrl = this.router.url;
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.previousUrl = this.currentUrl;
        this.currentUrl = event.url;
      }
    });
  }

  onEdit(node: any) {
    if (node.isFile) {
      this.triggerEditCollaboraOnline(node);
    }
  }

  onView(node: any) {
    if (node.isFile) {
      this.triggerViewWithCollaboraOnline(node);
    }
  }

  private triggerEditCollaboraOnline(node: any) {
    this.router.navigate(['/collabora-online', 'edit', node.id]);
  }

  private triggerViewWithCollaboraOnline(node: any) {
    this.router.navigate(['/collabora-online', 'view', node.id]);
  }

  /**
   * En-tetes d'appel aux webscripts `lool` du repository.
   *
   * Ces webscripts ne sont pas couverts par l'intercepteur d'ADF : l'en-tete est pose a la main.
   */
  private loolHeaders(): HttpHeaders {
    return new HttpHeaders().append('Authorization', collaboraAuthorizationHeader(this.authService));
  }

  /**
   * URL du serveur Collabora, telle que le repository l'annonce.
   *
   * `firstValueFrom` plutot qu'un `new Promise` enveloppant un `subscribe` : le resultat est le
   * meme, sans le detour ni le risque d'oublier un chemin d'erreur.
   */
  public getLoolUrl(): Promise<string> {
    const endpointUrl = window.location.origin + '/alfresco/s/lool/host/url';

    return firstValueFrom(this.http.get<{ lool_host_url: string }>(endpointUrl, { headers: this.loolHeaders() })).then(
      (response) => response.lool_host_url
    );
  }

  public getAccessToken(nodeId: string, action: string): Promise<LoolToken> {
    const endpointUrl = window.location.origin + '/alfresco/s/lool/token?nodeRef=workspace://SpacesStore/' + nodeId + '&action=' + action;

    return firstValueFrom(this.http.get<LoolToken>(endpointUrl, { headers: this.loolHeaders() }));
  }

  public getPreviousUrl() {
    return this.previousUrl;
  }

  public getExtensions() {
    const enable: boolean = this.appConfig.get<boolean>('collabora.enable');
    if (!enable) {
      return [];
    }
    let extCanView: string[] = this.appConfig.get<string[]>('collabora.view');
    extCanView = extCanView.concat(supportedViewExtensions);
    let extCanEdit: string[] = this.appConfig.get<string[]>('collabora.edit');
    extCanEdit = extCanEdit.concat(supportedEditExtensions);

    return extCanEdit.concat(extCanView);
  }
}
